import { FC, useEffect, useMemo, useState } from 'react';

import { cva } from 'class-variance-authority';
import type { MultiValue } from 'react-select';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import Select from 'react-select';

export interface Option {
  readonly value: string;
  readonly label: string;
}

interface PROPS {
  options: Option[];
  label?: string;
  className?: string;
  labelSubText?: string;
  subText?: string;
  textType?: 'default' | 'danger' | 'success' | 'warning';
  defaultValue?: string[];
  onChange: (val: MultiValue<Option>) => void;
  hideLabel?: boolean;
  size?: SIZE;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const controlClasses = cva(
  ['w-full !bg-fill-secondary', 'text-sm', '!shadow-none'],
  {
    variants: {
      size: {
        [SIZE.Small]: ['py-0'],
        [SIZE.Medium]: ['py-[3px]'],
        [SIZE.Large]: ['py-[3px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Medium,
    },
  },
);

const subTextClasses = cva(['text-xs'], {
  variants: {
    textType: {
      default: [],
      success: ['text-primary-base'],
      danger: ['text-danger-base'],
      warning: ['text-warning-base'],
    },
    size: {
      default: 'text-xs',
      [SIZE.Small]: 'text-xs',
      [SIZE.Medium]: 'text-xs',
      [SIZE.Large]: 'text-sm',
    },
  },
  defaultVariants: {
    textType: 'default',
  },
});

const MultiSelect: FC<PROPS> = ({
  label,
  labelSubText,
  className,
  onChange,
  defaultValue,
  hideLabel,
  size,
  required,
  placeholder,
  disabled,
  options,
  subText,
  textType,
}) => {
  const [value, setValue] = useState<Option[]>([]);
  const [input, setInput] = useState('');

  const _defaultValue = useMemo(
    () => options.filter((opt) => defaultValue?.includes(opt.value)),
    [defaultValue, options],
  );

  const handleChange = (val: MultiValue<Option>) => {
    setValue(val as Option[]);
    onChange(val);
    setInput('');
  };

  const handleInputChange = (e: string) => {
    setInput(e);
  };

  useEffect(() => {
    if (options.length && !value.length) {
      setValue(options.filter((opt) => defaultValue?.includes(opt.value)));
    }
    // adding dependencies causes infinite rerender
  }, [defaultValue, options, value.length]);

  return (
    <div className={cn(className)}>
      {!hideLabel && (
        <p className="text-sm font-semibold mb-2.5">
          {label ?? 'Tags'}
          {required ? (
            <span className="text-danger-base">*</span>
          ) : (
            <span className="text-xs text-content font-normal ml-0.5">
              {labelSubText ?? '(Optional)'}
            </span>
          )}
        </p>
      )}
      <Select
        defaultValue={_defaultValue}
        // value={value}
        isMulti
        isDisabled={disabled}
        placeholder={placeholder ?? 'Select'}
        options={options}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={input}
        classNames={{
          control: (state) =>
            cn(
              controlClasses({ size }),
              state.isFocused
                ? '!border-primary-base'
                : '!border-separator-secondary',
            ),
          menu: () => cn('py-2.5'),
          option: ({ isFocused, isSelected }) => {
            return cn(
              '!cursor-pointer px-5',
              '!text-sm text-content',
              'hover:!bg-transparent hover:font-semibold',
              isFocused ? '!text-primary-base' : '',
              isSelected && '',
              '!bg-transparent',
            );
          },
          multiValue: () =>
            cn(
              'py-1 px-2 !bg-fill-primary',
              '!flex !items-center !justify-between',
              '!border !border-separator-primary !rounded',
            ),
          multiValueLabel: () => cn('!text-sm !p-0 font-semibold'),
          multiValueRemove: () =>
            cn('!p-0 !h-[12px] !w-[12px] ml-0.5', 'flex-shrink-0'),
          indicatorSeparator: () => '!hidden',
        }}
      />
      {subText && (
        <div className={cn(subTextClasses({ textType, size }))}>{subText}</div>
      )}
    </div>
  );
};

export default MultiSelect;
